import { useMemo } from "react";
import type { ExploreResultItem } from "@/types/exploreResult";

/**
 * A named section of Explore results.
 * Sections provide curated rhythm: "Nära dig" → "Just nu" → "Utvalt" → "Alla".
 * See docs/design-rules/ux-rules/explore-12-10-sot.md §3
 */
export interface ExploreSection {
  key: string;
  title: string;
  subtitle?: string;
  items: ExploreResultItem[];
}

const NEAR_ME_MAX = 6;
const JUST_NOW_MAX = 3;
const HANDLA_MAX = 5;
const CURATED_MAX = 8;

/**
 * Splits a flat results list into curated sections for Explore.
 *
 * Rules:
 * - "Nära dig" only shows when user has active location
 * - "Just nu" shows date_time activities with upcoming dates
 * - "Utvalt för dig" uses localStorage history (tags/IDs)
 *   - Tag-match ranked higher than id-repeat
 * - "Alla" is always last and contains all remaining items
 * - An item may appear in a curated section AND in "Alla"
 * - Sections with 0 items are still returned but hidden by ExploreSectionList
 */
export function useExploreSections(
  results: ExploreResultItem[],
  options: {
    hasLocation: boolean;
    curatedTags: string[];
    curatedIds: Set<string>;
    mode: "places" | "farms" | "do";
  }
): ExploreSection[] {
  const { hasLocation, curatedTags, curatedIds, mode } = options;

  return useMemo(() => {
    const sections: ExploreSection[] = [];

    // ── 1. Nära dig (only with active location, distance-sorted) ──
    if (hasLocation) {
      const nearMe = results
        .filter((r) => r._distanceKm != null && r._distanceKm <= 25)
        .sort((a, b) => (a._distanceKm ?? Infinity) - (b._distanceKm ?? Infinity))
        .slice(0, NEAR_ME_MAX);

      if (nearMe.length > 0) {
        sections.push({
          key: "near-me",
          title: "Nära dig",
          subtitle: "Platser och upplevelser i din närhet",
          items: nearMe,
        });
      }
    }

    // ── 2. Just nu (date_time activities with upcoming dates) ──
    if (mode !== "farms") {
      const now = Date.now();
      const justNow = results
        .filter((r) => {
          if (r.kind !== "activity") return false;
          if (r.timingMode !== "date_time") return false;
          if (!r.startDate) return false;
          const start = new Date(r.startDate).getTime();
          // Within next 14 days
          return start >= now && start <= now + 14 * 24 * 60 * 60 * 1000;
        })
        .slice(0, JUST_NOW_MAX);

      if (justNow.length > 0) {
        sections.push({
          key: "just-now",
          title: "Just nu",
          subtitle: "Kommande upplevelser de närmaste dagarna",
          items: justNow,
        });
      }
    }

    // ── 3. Smaka & handla på gården (event-level includes_farm_sale) ──
    if (mode === "do") {
      const now = Date.now();
      const handlaItems = results
        .filter((r) => {
          if (r.kind !== "activity") return false;
          if (!r.includesFarmSale) return false;
          // Only future date_time or always-available
          if (r.timingMode === "date_time" && r.startDate) {
            return new Date(r.startDate).getTime() >= now;
          }
          return true; // anytime/season always qualify
        })
        .slice(0, HANDLA_MAX);

      if (handlaItems.length > 0) {
        sections.push({
          key: "handla",
          title: "Smaka & handla på gården",
          subtitle: "Upplevelser där du kan smaka något och kanske ta med något hem från gården.",
          items: handlaItems,
        });
      }
    }

    // ── 4. Utvalt för dig (localStorage-based, tag-match > id-repeat) ──
    if (curatedTags.length > 0 || curatedIds.size > 0) {
      const curatedTagSet = new Set(curatedTags.map((t) => t.toLowerCase()));

      // Score: tag match = 2, id match = 1
      const scored = results
        .map((r) => {
          let score = 0;
          if (r.badges.some((b) => curatedTagSet.has(b.toLowerCase()))) score += 2;
          if (curatedIds.has(r.id)) score += 1;
          return { item: r, score };
        })
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, CURATED_MAX)
        .map((s) => s.item);

      if (scored.length >= 2) {
        sections.push({
          key: "curated",
          title: "Utvalt för dig",
          items: scored,
        });
      }
    }

    // ── 5. Alla (always present, full list) ──
    if (results.length > 0) {
      const allTitle =
        mode === "do" ? "Alla aktiviteter" : mode === "farms" ? "Alla gårdar" : "Alla platser";
      sections.push({
        key: "all",
        title: allTitle,
        items: results,
      });
    }

    return sections;
  }, [results, hasLocation, curatedTags, curatedIds, mode]);
}
